/**
 * One pure builder per landing section. Each returns a detached HTMLElement;
 * Landing.ts mounts them in order. No state, no WebGL — the hero backdrop is
 * pure CSS (see `.ls-hero-*` in style.css).
 */
import {
  COPYRIGHT,
  FEATURES,
  FLORA_BESTIARY,
  FOOTER_BLURB,
  FOOTER_COLUMNS,
  GUARDIAN_BESTIARY,
  HERD_BESTIARY,
  TAGLINE,
  type BestiaryEntry,
} from './copy';
import { STORY_BEATS } from './story';

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (className) n.className = className;
  if (text != null) n.textContent = text;
  return n;
}

function section(id: string, className: string): HTMLElement {
  const s = el('section', className);
  s.id = id;
  return s;
}

function sectionHead(kicker: string, heading: string): HTMLElement {
  const head = el('div', 'ls-head');
  head.append(el('div', 'ls-kicker', kicker), el('h2', 'ls-h2', heading));
  return head;
}

/** Point poster at posterSrc; swap to fallback if it 404s. */
function withPoster(video: HTMLVideoElement, posterSrc: string, fallback: string): void {
  video.poster = posterSrc;
  const probe = new Image();
  probe.onerror = () => {
    video.poster = fallback;
  };
  probe.src = posterSrc;
}

/**
 * Hero: full viewport, a pure-CSS Cretaceous scene behind it (sky gradient with
 * the red sun and the rising second light, layered tree-line silhouettes, fog,
 * ember drift — all in style.css). Title, tagline, PLAY, the loading bar, the
 * graphics selector, a scroll cue.
 */
/**
 * Top navigation: logo + wordmark left, section anchors and PLAY right.
 * Transparent over the hero, gains a solid backdrop once scrolled (`.scrolled`,
 * toggled by Landing). `playBtn` is a second PLAY node built by Landing.
 */
export function navBar(playBtn: HTMLElement): HTMLElement {
  const nav = el('nav', 'ls-nav');
  nav.setAttribute('aria-label', 'Primary');

  const brand = el('a', 'ls-nav-brand') as HTMLAnchorElement;
  brand.href = '#ls-hero';
  brand.innerHTML =
    '<span class="ls-nav-logo" aria-hidden="true"></span><span class="ls-nav-word">EXTINCTRUM</span>';

  const links = el('div', 'ls-nav-links');
  const anchors: [string, string][] = [
    ['#ls-story', 'Story'],
    ['#ls-trailer', 'Teaser'],
    ['#ls-about', 'About'],
    ['#ls-bestiary', 'Bestiary'],
    ['#ls-feedback', 'Feedback'],
    ['#ls-footer', 'The preview'],
  ];
  for (const [href, label] of anchors) {
    const a = el('a', 'ls-nav-link') as HTMLAnchorElement;
    a.href = href;
    a.textContent = label;
    links.appendChild(a);
  }

  nav.append(brand, links, playBtn);
  return nav;
}

/** The fixed jungle backdrop — mounted as a direct child of #landing, behind
 * every section. */
export function sceneLayer(): HTMLElement {
  const scene = el('div', 'ls-scene');
  scene.setAttribute('aria-hidden', 'true');
  scene.append(
    el('div', 'ls-sky'),
    el('div', 'ls-sun'),
    el('div', 'ls-impactor'), // god-rays through the canopy
    el('div', 'ls-ridge ls-ridge-far'),
    el('div', 'ls-ridge ls-ridge-mid'),
    el('div', 'ls-ridge ls-ridge-near'),
    el('div', 'ls-canopy-l'),
    el('div', 'ls-canopy-r'),
    el('div', 'ls-fog'),
    el('div', 'ls-embers'), // drifting spores
    el('div', 'ls-scene-scrim'),
  );
  return scene;
}

export function heroSection(
  playBtn: HTMLElement,
  identityEl: HTMLElement,
  graphicsEl: HTMLElement,
  loadingBar: HTMLElement,
): HTMLElement {
  const s = section('ls-hero', 'ls-hero');

  const inner = el('div', 'ls-hero-inner');
  const title = el('div', 'landing-title', 'EXTINCTRUM');
  const episode = el('div', 'landing-episode', 'I · THE CRETACEOUS');
  const tagline = el('p', 'ls-tagline', TAGLINE);

  const cta = el('div', 'ls-cta');
  cta.append(playBtn, loadingBar);

  const cue = el('div', 'ls-scroll-cue', 'read on ↓');

  inner.append(title, episode, tagline, cta, identityEl, graphicsEl, cue);
  s.append(inner);
  return s;
}

export function trailerSection(
  trailerSrc: string,
  posterSrc: string,
  posterFallback: string,
): HTMLElement {
  const s = section('ls-trailer', 'ls-section ls-trailer');
  s.append(sectionHead('TEASER', 'Watch the teaser'));

  const frame = el('div', 'ls-trailer-frame');
  const video = el('video', 'ls-trailer-video') as HTMLVideoElement;
  video.controls = true;
  video.loop = true;
  video.playsInline = true;
  video.preload = 'metadata';
  withPoster(video, posterSrc, posterFallback);
  const source = el('source') as HTMLSourceElement;
  source.src = trailerSrc;
  source.type = trailerSrc.endsWith('.webm') ? 'video/webm' : 'video/mp4';
  video.appendChild(source);
  frame.appendChild(video);
  wireTrailerAutoplay(frame, video);
  s.appendChild(frame);
  return s;
}

/**
 * The teaser plays — with sound — while it is on screen, and pauses when it
 * scrolls away so no audio comes from a section nobody is looking at. A
 * visitor who has scrolled this far has almost always satisfied the browser's
 * autoplay gesture gate; if not, we drop to muted playback and float a
 * one-tap unmute over the frame.
 */
function wireTrailerAutoplay(frame: HTMLElement, video: HTMLVideoElement): void {
  if (typeof IntersectionObserver !== 'function') return;

  let unmuteBtn: HTMLButtonElement | null = null;
  const offerUnmute = () => {
    if (unmuteBtn) return;
    const btn = el('button', 'ls-trailer-unmute', '🔊 Tap for sound') as HTMLButtonElement;
    btn.type = 'button';
    btn.setAttribute('data-unmute', '');
    btn.addEventListener('click', () => {
      video.muted = false;
      void video.play().catch(() => {});
      btn.remove();
      unmuteBtn = null;
    });
    frame.appendChild(btn);
    unmuteBtn = btn;
  };

  const playWithSound = () => {
    video.muted = false;
    video.play().catch(() => {
      video.muted = true;
      void video.play().catch(() => {});
      offerUnmute();
    });
  };

  const obs = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) playWithSound();
      else if (!video.paused) video.pause();
    },
    { threshold: 0.5 },
  );
  obs.observe(video);
}

/**
 * Story: the premise as a vertical timeline of beats. Each beat carries
 * `.ls-reveal` and animates in when scrolled into view (Landing wires the
 * observer).
 */
export function storySection(): HTMLElement {
  const s = section('ls-story', 'ls-section ls-story');
  s.append(sectionHead('THE PREMISE', 'One dawn on the Hell Creek delta'));

  const timeline = el('div', 'ls-timeline');
  STORY_BEATS.forEach((beat, i) => {
    const item = el('div', 'ls-beat ls-reveal');
    item.style.setProperty('--i', String(i));
    item.append(
      el('div', 'ls-beat-dot'),
      (() => {
        const c = el('div', 'ls-beat-content');
        c.append(el('h3', 'ls-beat-lead', beat.lead), el('p', 'ls-beat-body', beat.body));
        return c;
      })(),
    );
    timeline.appendChild(item);
  });
  s.appendChild(timeline);
  return s;
}

/** About: the pillar features as reveal-on-scroll cards. */
export function aboutSection(): HTMLElement {
  const s = section('ls-about', 'ls-section ls-about');
  s.append(sectionHead('WHAT IT IS', 'Survival, not combat'));

  const grid = el('div', 'ls-pillars');
  FEATURES.forEach((f, i) => {
    const card = el('article', 'ls-pillar ls-reveal');
    card.style.setProperty('--i', String(i));
    card.append(el('h3', 'ls-pillar-h', f.title), el('p', 'ls-pillar-b', f.body));
    grid.appendChild(card);
  });
  s.appendChild(grid);
  return s;
}

function bestiaryCard(entry: BestiaryEntry, i: number): HTMLElement {
  const card = el('article', 'ls-creature ls-reveal');
  card.style.setProperty('--i', String(i));

  const img = el('img', 'ls-creature-img') as HTMLImageElement;
  img.src = entry.image;
  img.alt = entry.name;
  img.loading = 'lazy';
  img.decoding = 'async';
  img.onerror = () => card.classList.add('ls-creature-noimg');

  const text = el('div', 'ls-creature-text');
  text.append(
    el('h4', 'ls-creature-name', entry.name),
    el('div', 'ls-creature-role', entry.role),
    el('p', 'ls-creature-body', entry.body),
  );

  card.append(img, text);
  return card;
}

function bestiaryGroup(kicker: string, entries: readonly BestiaryEntry[], offset: number): HTMLElement {
  const group = el('div', 'ls-creature-group');
  const label = el('div', 'ls-creature-group-h', kicker);
  const grid = el('div', 'ls-creature-grid');
  entries.forEach((e, i) => grid.appendChild(bestiaryCard(e, offset + i)));
  group.append(label, grid);
  return group;
}

/**
 * Bestiary: the seven-species roster plus a flora sampler, each with a hero
 * image where one exists (`copy.ts`'s `image` field — some species only ever
 * had a public-domain reference plate rather than an AI-rendered preview; a
 * broken image just drops the card into text-only via `ls-creature-noimg`).
 */
export function bestiarySection(): HTMLElement {
  const s = section('ls-bestiary', 'ls-section ls-bestiary');
  s.append(sectionHead('THE FLOODPLAIN', 'Who — and what — is out there'));

  s.append(
    bestiaryGroup('GUARDIANS — ONE PER CORE', GUARDIAN_BESTIARY, 0),
    bestiaryGroup('THE HERD', HERD_BESTIARY, GUARDIAN_BESTIARY.length),
    bestiaryGroup('FLORA', FLORA_BESTIARY, GUARDIAN_BESTIARY.length + HERD_BESTIARY.length),
  );
  return s;
}

/**
 * Feedback: a bug-report / feedback form over the same sheet treatment as the
 * other sections. The form element itself is built by `landing/feedback.ts`
 * (it owns the submit logic); this only frames it, like `heroSection` does
 * for the identity and graphics controls.
 */
export function feedbackSection(form: HTMLElement): HTMLElement {
  const s = section('ls-feedback', 'ls-section ls-feedback');
  s.append(sectionHead('FEEDBACK', 'Found a bug? Tell us'));
  const intro = el('p', 'ls-feedback-intro', 'This is a live preview — your report goes straight to the developers, with your graphics tier and device info attached.');
  intro.classList.add('ls-reveal');
  form.classList.add('ls-reveal');
  s.append(intro, form);
  return s;
}

/**
 * Footer: a brand column plus link columns, then a bottom bar with the
 * copyright line. Two-column link grid on desktop, stacked on narrow screens.
 */
export function footerSection(): HTMLElement {
  const s = section('ls-footer', 'ls-footer');

  const top = el('div', 'ls-footer-top');

  const brand = el('div', 'ls-footer-brand');
  brand.append(
    el('div', 'ls-footer-word', 'EXTINCTRUM'),
    el('div', 'ls-footer-ep', 'I · THE CRETACEOUS'),
    el('p', 'ls-footer-blurb', FOOTER_BLURB),
  );
  top.appendChild(brand);

  const cols = el('div', 'ls-footer-cols');
  for (const col of FOOTER_COLUMNS) {
    const c = el('div', 'ls-footer-col');
    c.appendChild(el('div', 'ls-footer-col-h', col.heading));
    const list = el('ul', 'ls-footer-list');
    for (const l of col.links) {
      const li = el('li');
      const a = el('a', 'ls-footer-link') as HTMLAnchorElement;
      a.href = l.href;
      a.textContent = l.label;
      if (l.external) {
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
      }
      if (l.soon) a.classList.add('is-soon');
      li.appendChild(a);
      list.appendChild(li);
    }
    c.appendChild(list);
    cols.appendChild(c);
  }
  top.appendChild(cols);
  s.appendChild(top);

  const bar = el('div', 'ls-footer-bar');
  bar.append(
    el('span', 'ls-footer-copy', COPYRIGHT),
    el('span', 'ls-footer-tag', 'Play free in your browser'),
  );
  s.appendChild(bar);

  return s;
}
